import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    CloseFiscalYearResponse,
    PeriodWriteResponse,
    ReopenFiscalYearResponse,
    RolloverResponse,
} from '@/api/handlers/resources/periods/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { invalidateAfterPeriodWrite } from './invalidateAfterPeriodWrite';

/**
 * The write paths of the periods surface.
 *
 * Every one takes a bare **id** — there is no request body anywhere in this
 * file, and no way to supply one. That is the structural guarantee behind "no
 * `source` field is ever sent": `CLOSING` and `ROLLOVER` are server-assigned,
 * and there is nothing for a caller to put them in.
 *
 * All of them invalidate periods + journal + statements together, because a
 * close moves all three (see `invalidateAfterPeriodWrite`). None of them reject
 * — callers branch on `result.success`.
 *
 * `useRolloverMutation` is still here and still real, but **nothing calls it**:
 * the API has no rollover dry run, so the screen routes that action to
 * `LedgerHandoffNotice` instead. Kept so reopening it is a one-line change.
 */

/** Close one month. */
export const useClosePeriodMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<PeriodWriteResponse>, Error, string>({
        mutationFn: (periodId) => apiClient.periods.closePeriod(periodId),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterPeriodWrite(queryClient);
            }
        },
    });
};

/** Reopen one closed month. */
export const useReopenPeriodMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<PeriodWriteResponse>, Error, string>({
        mutationFn: (periodId) => apiClient.periods.reopenPeriod(periodId),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterPeriodWrite(queryClient);
            }
        },
    });
};

/** Post the year's CLOSING entry. Consequential — always confirm first. */
export const useCloseFiscalYearMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CloseFiscalYearResponse>, Error, string>({
        mutationFn: (fiscalYearId) =>
            apiClient.periods.closeFiscalYear(fiscalYearId),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterPeriodWrite(queryClient);
            }
        },
    });
};

/** Post the ROLLOVER entry: current-year result → retained earnings. */
export const useRolloverMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<RolloverResponse>, Error, string>({
        mutationFn: (fiscalYearId) =>
            apiClient.periods.postRetainedEarningsRollover(fiscalYearId),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterPeriodWrite(queryClient);
            }
        },
    });
};

/**
 * Reopen a closed year by posting reversing entries. Not an undo — the
 * originals stay in the journal marked REVERSED.
 */
export const useReopenFiscalYearMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<ReopenFiscalYearResponse>, Error, string>({
        mutationFn: (fiscalYearId) =>
            apiClient.periods.reopenFiscalYear(fiscalYearId),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterPeriodWrite(queryClient);
            }
        },
    });
};
